/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, {useState, useCallback, useEffect, useMemo} from 'react';
// redux
import {useSelector, useDispatch} from 'react-redux';
import {updateSiteEmojiCategories} from "../../../../../state/thunks/engagement";
// material
import {InlineMessage, SingleSelectDropDown} from '@insticator/insticator-ui';
import ExpandableSection from '../../../../../material/expandablesection/expandablesection';
import ConfigController from '../../../../../material/expandablesection/controllers/configcontroller/configcontroller';
import SectionCard from '../../../../../material/expandablesection/sectioncard/sectioncard';
import SectionDescription from '../../../../../material/expandablesection/sectiondescription/sectiondescription';
import RowItem, {LIMIT_ACTIVE_EMOJIS} from './rowitem/rowitem';
import EmojiIcon from '../../../../../material/emojiicon/emojiicon';
// data
import {Emojis, EmojiCategories, EmojiCategoriesTitles} from "./emojiList";
// styles
import injectSheet from 'react-jss';
import styles from './emoji_styles';
import EmojiFilterMenu from "./filter/emojifiltermenu";

/* ========== ~~~~~~~~~~ EMOJI CONFIG ~~~~~~~~~~ ========== */
const EmojiConfig = injectSheet(styles)((props) => {
  const dispatch = useDispatch();
  const {
    engagement: {
      emojiConfigurations,
      emojisCategories,
      isLoading,
    }
  } = useSelector((state) => state);

  const [isOpened, setIsOpened] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [config, setConfig] = useState([]);
  const [defaultConfig, setDefaultConfig] = useState(config);
  const [filteredCategories, setFilteredCategories] = useState(EmojiCategories)
  const [emojiSelection, setEmojiSelection] = useState('')

  useEffect(async () => {
    // fetch config
    setConfig(emojiConfigurations)
    setDefaultConfig(emojiConfigurations)
  }, [emojiConfigurations])

  useEffect(async () => {
    let emojiCategoryType = ''

    if (config?.emojis?.length > 0) {
      const categoryType = config?.emojis[0]?.emojiCategory?.emojiCategoryType;
      const isEmojisHaveSameCategoryType = config?.emojis?.every(e => categoryType === e?.emojiCategory?.emojiCategoryType);
      emojiCategoryType = isEmojisHaveSameCategoryType ? categoryType : emojiCategoryType
    }

    setEmojiSelection(emojiCategoryType)
  }, [config?.emojis])

  const isLimitActiveEmojis = useMemo(() =>
      config?.emojis?.filter((e) => e.active)?.length <= LIMIT_ACTIVE_EMOJIS
    , [config?.emojis])

  const handleSetEmojiCategory = useCallback((value) => {
    // const {value} = e.target

    if (!value) return

    const newEmojis = config?.emojis?.map((e) => {
      const {
        emojiCategory: {
          emojiCategoryType,
          emojiName: eName,
        } = {}
      } = e || {};

      const emojiCategory = emojisCategories?.find(({emojiCategoryType, emojiName}) =>
        emojiName === eName && emojiCategoryType === value
      )

      return emojiCategoryType === value
        ? e
        : {...e, emojiCategory}
    })

    setEmojiSelection(value)
    setConfig({
      ...config, emojis: newEmojis
    });
    setIsChanged(true)
  }, [config, emojisCategories])

  const handleSelectEmojiCategory = useCallback((emojiName, emojiCategoryType, emojiUUID) => {
    const emojiCategory = emojisCategories?.find(c =>
      c.emojiName.toLowerCase() === emojiName.toLowerCase() && c.emojiCategoryType === emojiCategoryType
    );
    const newEmojis = config?.emojis?.map((e) => e.emojiUUID === emojiUUID ? {...e, emojiCategory} : e)
    setConfig({...config, emojis: newEmojis});
    setIsChanged(true)
  }, [config, emojisCategories])

  const handleActivateEmoji = useCallback((active, emojiUUID) => {
    const oldConfig = config
    const newEmojis = config?.emojis?.map((e) => e.emojiUUID === emojiUUID ? {...e, active} : e)
    setConfig({...config, emojis: newEmojis})
    setIsChanged(true)

    // I had to do this workaround because of state and animation issue
    if (isLimitActiveEmojis && !active) {
      setTimeout(() => setConfig(oldConfig), 250)
    }
  }, [config?.emojis, isLimitActiveEmojis])

  const handleToggle = useCallback(() => {
    setIsOpened(!isOpened)
  }, [isOpened]);

  const handleCancel = useCallback(() => {
    setIsOpened(false);
    setIsChanged(false);
    setConfig(defaultConfig)
  }, [defaultConfig]);

  const handleSave = useCallback(async () => {
    const {
      siteUUID,
    } = config;
    const emojis = config?.emojis?.map(e => ({
      emojiUUID: e?.emojiUUID,
      emojiCategoryUUID: e?.emojiCategory?.emojiCategoryUUID,
      active: e?.active,
    }));
    await dispatch(updateSiteEmojiCategories({siteUUID, emojis}))
    setIsOpened(false)
    setIsChanged(false)
  }, [config, dispatch]);

  const getEmojiConfigByName = useCallback(name =>
      config?.emojis?.find(e =>
        name?.toLowerCase() === e?.emojiCategory?.emojiName?.toLowerCase()
      ) || {},
    [config?.emojis]
  );

  const handleFilterCategories = useCallback(({categories}) =>
      setFilteredCategories(categories)
    , [])

  const items = EmojiCategories?.map(value => ({
    value,
    name: EmojiCategoriesTitles[value],
    text: EmojiCategoriesTitles[value],
  }));

  const controller = (
    <ConfigController
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isOpened={isOpened}
      isChanged={isChanged}
      isLoading={isLoading}
      title='Emojis'
      description={!isOpened && <SectionDescription
        main={'Emojis in Production'}
        content={
          <div className={'emojilist'}>
            {Emojis.map((e) => {
                const {emojiCategory, active} = getEmojiConfigByName(e.name)
                if (!active) return null;
                return (
                  <EmojiIcon key={e.name} src={e[emojiCategory?.emojiCategoryType]} />
                )
              }
            )}
          </div>
        }
      />}
    />
  );

  // removed this and added <EmojiIcon /> above in controller function
  // <img
  //   style={{
  //     maxHeight: '32px',
  //     display: 'inline-block',
  //     margin: '0 10px'
  //   }}
  //   key={e.name}
  //   src={e[emojiCategory?.emojiCategoryType]}
  // />

  return (
    <ExpandableSection
      className={`emojiconfig ${props.classes.emojiconfig}`}
      controller={controller}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isOpened={isOpened}
      isChanged={isChanged}
      isLoading={isLoading}
    >
      {isOpened && <>
        <SectionCard klass={'messagecard'}>
          <InlineMessage
            name={'emojimessage'}
            variant={'none'}
            icon={'exclamation-triangle'}
            message={'When configuring the emojis you want to show on your site, your selection and/or edits of emojis to use will always be applicable to both Article Sentiment and Commenting solutions.'}
            display={true}
            title={''}
            close={false}
          />
        </SectionCard>
        <SectionCard>
          <table className={'emojidata'}>
            <tr>
              <th><div>Emoji</div></th>
              <th><div>Status</div></th>
              <th>
                <div
                  className="emojidropdown">
                  {/* <div className="formelement">
                    <span className={'selectWrapper'}>
                      <i className="fas fa-sort-down "></i>
                      <select
                        onChange={handleSetEmojiCategory}
                        className="select SelectField"
                        type="text">
                        {[
                          {value: '', name: 'Current Selection'},
                          ...items
                        ]?.map(o => <option
                          selected={o.value === emojiSelection}
                          key={o.name}
                          value={o.value}
                          disabled={!o.value}
                        >{o.name}</option>)}
                      </select>

                
                    </span>
                  </div> */}
                    <SingleSelectDropDown 
                      name={EmojiCategoriesTitles[emojiSelection]} 
                      searchable={false} 
                      onSelect={selected => handleSetEmojiCategory(selected.value)} 
                      items={items.map(item => ({...item, text: ''}))}
                    />
                </div>
              </th>
              <th>
                <EmojiFilterMenu
                  name={'filtermenu'}
                  filters={
                    {
                      categories: {
                        title: "",
                        icon: false,
                        initialState: filteredCategories,
                        items,
                      },
                    }}
                  onApply={handleFilterCategories}/>
              </th>
            </tr>
            {Emojis.map(e => <RowItem
                key={e.name}
                {...getEmojiConfigByName(e.name)}
                handleActivateEmoji={handleActivateEmoji}
                handleSelectEmojiCategory={handleSelectEmojiCategory}
                filteredCategories={EmojiCategories.filter(category => filteredCategories.includes(category))}
                isLimitActiveEmojis={isLimitActiveEmojis}
              />
            )}
          </table>
          <InlineMessage
            name={'emojimessage'}
            variant={'none'}
            icon={'exclamation-triangle'}
            message={`At least ${LIMIT_ACTIVE_EMOJIS} emojis must be enabled`}
            display={true}
            title={''}
            close={false}
          />
        </SectionCard>
      </>}
    </ExpandableSection>
  );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default EmojiConfig;
